import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { questionsAPI } from "../services/apiService";
import { useSEO } from "../hooks/useSEO";
import { initializeTracking, trackPasswordVerification } from "../utils/trackingUtils";

const TestIQ = () => {
  // Use SEO hook for IQ Test page
  const { seoData } = useSEO("iq", {
    h1: "Test IQ Online - Kiểm tra chỉ số thông minh",
    metaDescription:
      "Làm bài test IQ online với 25 câu hỏi chuẩn quốc tế. Đo lường chỉ số thông minh IQ của bạn một cách chính xác.",
    title: "Test IQ Online - Kiểm tra chỉ số thông minh miễn phí",
  });
  const navigate = useNavigate();
  const [currentScreen, setCurrentScreen] = useState("intro"); // intro, test, completed, result
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [timeLeft, setTimeLeft] = useState(1200); // 20 minutes
  const [password, setPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [iqScore, setIqScore] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [reviewLaterQuestions, setReviewLaterQuestions] = useState([]);
  const [isAutoAdvancing, setIsAutoAdvancing] = useState(false);
  const [showIncompleteModal, setShowIncompleteModal] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [showQuestionBoard, setShowQuestionBoard] = useState(false);
  const [showNeedAnswerNotification, setShowNeedAnswerNotification] =
    useState(false); // New state for notification
  const [isReviewingMode, setIsReviewingMode] = useState(false); // New state to track reviewing mode
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  // Load mật khẩu từ localStorage hoặc sử dụng mặc định
  const [correctPassword, setCorrectPassword] = useState("iq2025");

  // Load password from localStorage
  useEffect(() => {
    const savedTestPasswords = localStorage.getItem("test_passwords");
    if (savedTestPasswords) {
      const passwords = JSON.parse(savedTestPasswords);
      setCorrectPassword(passwords.iq || "iq2025");
    }
  }, []);

  // Load questions from API database
  useEffect(() => {
    const loadQuestions = async () => {
      try {
        setIsLoading(true);
        setLoadError(null);
        console.log("🔍 Loading IQ questions from API...");

        const apiQuestions = await questionsAPI.getByType("iq");
        console.log(`✅ Raw IQ API response:`, apiQuestions);

        if (
          apiQuestions &&
          Array.isArray(apiQuestions) &&
          apiQuestions.length > 0
        ) {
          // Transform API data to expected format
          const transformedQuestions = apiQuestions.map((q, index) => {
            console.log("🔍 Processing IQ question:", q.question);
            console.log("🔍 Raw IQ options:", q.options);

            // Validate and transform options
            let transformedOptions = [];

            if (Array.isArray(q.options) && q.options.length > 0) {
              transformedOptions = q.options.map((opt, optIndex) => {
                const transformed = {
                  text:
                    opt.option_text ||
                    opt.text ||
                    opt ||
                    `Option ${optIndex + 1}`,
                  value: opt.option_value || opt.value || opt || `${optIndex}`,
                };
                console.log("🔄 Transformed IQ option:", transformed);
                return transformed;
              });
            } else {
              // Fallback options if no options available
              transformedOptions = [
                { text: "A", value: "0" },
                { text: "B", value: "1" },
                { text: "C", value: "2" },
                { text: "D", value: "3" },
              ];
              console.warn(
                "⚠️ No options found for IQ question, using fallback"
              );
            }

            return {
              id: q.id || index + 1,
              question: q.question || `Question ${index + 1}`,
              options: transformedOptions,
              correctAnswer: Array.isArray(q.options)
                ? q.options.findIndex((opt) => opt.is_correct === 1)
                : 0,
            };
          });

          console.log(
            "🔄 Final transformed IQ questions:",
            transformedQuestions
          );
          setQuestions(transformedQuestions);
        } else {
          console.warn(
            "❌ No IQ questions found in database, loading defaults..."
          );
          const defaultQuestions = getDefaultQuestions();
          if (Array.isArray(defaultQuestions) && defaultQuestions.length > 0) {
            setQuestions(defaultQuestions);
          } else {
            throw new Error("Default questions not available");
          }
        }
      } catch (error) {
        console.error("❌ Error loading IQ questions:", error);
        setLoadError(error.message);
        // Fallback to default questions
        const defaultQuestions = getDefaultQuestions();
        if (Array.isArray(defaultQuestions) && defaultQuestions.length > 0) {
          setQuestions(defaultQuestions);
          console.log(
            `⚠️ Using fallback: ${defaultQuestions.length} default questions`
          );
        } else {
          // Final fallback - minimal questions
          setQuestions([
            {
              id: 1,
              question: "Trong dãy số sau, số tiếp theo là gì: 2, 4, 8, 16, ?",
              options: [
                { text: "24", value: "0" },
                { text: "32", value: "1" },
                { text: "30", value: "2" },
                { text: "28", value: "3" },
              ],
              correctAnswer: 1,
            },
          ]);
        }
      } finally {
        setIsLoading(false);
      }
    };

    loadQuestions();
  }, []);

  // Initialize tracking when component mounts
  useEffect(() => {
    initializeTracking('iq');
  }, []);

  // Default questions fallback function
  const getDefaultQuestions = () => {
    return [
      {
        id: 1,
        question: "Trong dãy số sau, số tiếp theo là gì: 2, 4, 8, 16, ?",
        options: ["24", "32", "30", "28"],
        correctAnswer: 1,
      },
      {
        id: 2,
        question:
          "Nếu tất cả các con mèo đều là động vật, và tất cả các động vật đều cần thức ăn, thì:",
        options: [
          "Tất cả các con mèo đều cần thức ăn",
          "Một số con mèo cần thức ăn",
          "Không con mèo nào cần thức ăn",
          "Không thể xác định",
        ],
        correctAnswer: 0,
      },
      {
        id: 3,
        question: "Từ nào không cùng nhóm với các từ còn lại?",
        options: ["Táo", "Cam", "Chuối", "Cà rót"],
        correctAnswer: 3,
      },
      {
        id: 4,
        question: "Nếu A = 1, B = 2, C = 3, thì CAB = ?",
        options: ["312", "321", "123", "132"],
        correctAnswer: 0,
      },
      {
        id: 5,
        question: "Trong dãy số: 1, 4, 9, 16, 25, số tiếp theo là:",
        options: ["30", "35", "36", "49"],
        correctAnswer: 2,
      },
      {
        id: 6,
        question:
          "Một hộp có 12 quả bóng đỏ và 8 quả bóng xanh. Xác suất lấy được 1 quả bóng đỏ là:",
        options: ["12/20", "8/20", "12/8", "8/12"],
        correctAnswer: 0,
      },
      {
        id: 7,
        question: "Nếu hôm nay là thứ Ba, thì 100 ngày nữa sẽ là thứ mấy?",
        options: ["Thứ Ba", "Thứ Tư", "Thứ Năm", "Thứ Sáu"],
        correctAnswer: 1,
      },
      {
        id: 8,
        question: "Số nào khác với các số còn lại?",
        options: ["144", "169", "196", "200"],
        correctAnswer: 3,
      },
      {
        id: 9,
        question: "Nếu MỘT = 123, HAI = 456, thì BA = ?",
        options: ["78", "67", "87", "76"],
        correctAnswer: 0,
      },
      {
        id: 10,
        question: "Trong dãy: Z, Y, X, W, V, chữ cái tiếp theo là:",
        options: ["U", "T", "S", "R"],
        correctAnswer: 0,
      },
      {
        id: 11,
        question:
          "Một người đi từ A đến B mất 3 giờ với vận tốc 60km/h. Quãng đường AB dài:",
        options: ["120km", "180km", "200km", "240km"],
        correctAnswer: 1,
      },
      {
        id: 12,
        question: "Số nào tiếp theo trong dãy: 3, 6, 12, 24, ?",
        options: ["36", "48", "40", "44"],
        correctAnswer: 1,
      },
      {
        id: 13,
        question: "Từ STUDY có thể tạo ra từ nào sau đây?",
        options: ["DUSTY", "RUSTY", "MISTY", "NASTY"],
        correctAnswer: 0,
      },
      {
        id: 14,
        question: "Nếu 5 + 5 = 30, 4 + 4 = 20, thì 3 + 3 = ?",
        options: ["12", "15", "18", "10"],
        correctAnswer: 0,
      },
      {
        id: 15,
        question:
          "Trong một lớp học có 30 học sinh, 18 học sinh học tiếng Anh, 12 học sinh học tiếng Pháp, 6 học sinh học cả hai. Có bao nhiêu học sinh không học ngôn ngữ nào?",
        options: ["4", "6", "8", "10"],
        correctAnswer: 1,
      },
      {
        id: 16,
        question: "Số nào là bội chung nhỏ nhất của 12 và 18?",
        options: ["36", "54", "72", "108"],
        correctAnswer: 0,
      },
      {
        id: 17,
        question: "Nếu tất cả A là B, và một số B là C, thì:",
        options: [
          "Tất cả A là C",
          "Một số A là C",
          "Không A nào là C",
          "Không thể xác định",
        ],
        correctAnswer: 3,
      },
      {
        id: 18,
        question: "Trong dãy chữ cái: A, C, F, J, O, chữ cái tiếp theo là:",
        options: ["T", "U", "V", "W"],
        correctAnswer: 1,
      },
      {
        id: 19,
        question:
          "Một chiếc xe hơi tiêu thụ 8 lít xăng cho 100km. Để đi 250km cần bao nhiêu lít xăng?",
        options: ["18 lít", "20 lít", "22 lít", "24 lít"],
        correctAnswer: 1,
      },
      {
        id: 20,
        question: "Số nào khác biệt trong dãy: 2, 5, 8, 11, 15, 17?",
        options: ["2", "5", "15", "17"],
        correctAnswer: 2,
      },
      {
        id: 21,
        question: "Nếu BIRD = 1234, và DIRT = 5367, thì RIDE = ?",
        options: ["3456", "3245", "3254", "3425"],
        correctAnswer: 0,
      },
      {
        id: 22,
        question: "Trong một hình vuông có diện tích 64 cm², chu vi là:",
        options: ["16 cm", "24 cm", "32 cm", "40 cm"],
        correctAnswer: 2,
      },
      {
        id: 23,
        question: "Số tiếp theo trong dãy: 1, 1, 2, 3, 5, 8, ?",
        options: ["11", "13", "15", "17"],
        correctAnswer: 1,
      },
      {
        id: 24,
        question: "Nếu ngày hôm qua là thứ Hai, thì ngày mai sẽ là:",
        options: ["Thứ Tư", "Thứ Năm", "Thứ Sáu", "Thứ Bảy"],
        correctAnswer: 0,
      },
      {
        id: 25,
        question: "Trong dãy: 100, 50, 25, 12.5, số tiếp theo là:",
        options: ["6.25", "5.5", "6", "7"],
        correctAnswer: 0,
      },
      {
        id: 26,
        question: "Từ nào có thể tạo thành từ các chữ cái của từ CREATION?",
        options: ["REACTION", "RELATION", "NATIONAL", "LOCATION"],
        correctAnswer: 0,
      },
    ];
  };

  // Timer countdown
  useEffect(() => {
    if (currentScreen === "test" && timeLeft > 0) {
      const timer = setTimeout(() => {
        setTimeLeft(timeLeft - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (timeLeft === 0 && currentScreen === "test") {
      calculateResult();
    }
  }, [timeLeft, currentScreen]);

  // Auto-hide notification after 5 seconds
  useEffect(() => {
    if (showNeedAnswerNotification) {
      const timer = setTimeout(() => {
        setShowNeedAnswerNotification(false);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [showNeedAnswerNotification]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const startTest = () => {
    setCurrentScreen("test");
    setCurrentQuestion(0);
    setAnswers([]);
    setTimeLeft(1200);
    setIsReviewingMode(false); // Reset reviewing mode when starting test
    setReviewLaterQuestions([]); // Clear review later questions
    // Scroll to top when starting test
    window.scrollTo(0, 0);
  };

  const handleAnswer = (answerIndex) => {
    const newAnswers = [...answers];
    newAnswers[currentQuestion] = answerIndex;
    setAnswers(newAnswers);
  };

  const nextQuestion = () => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
      setShowNeedAnswerNotification(false); // Hide notification when moving to next question
      setIsReviewingMode(false); // Exit reviewing mode when navigating manually
    }
  };

  const prevQuestion = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
      setShowNeedAnswerNotification(false); // Hide notification when moving to previous question
      setIsReviewingMode(false); // Exit reviewing mode when navigating manually
    }
  };

  const toggleReviewLater = () => {
    const newReviewLater = [...reviewLaterQuestions];
    const wasMarkedForReview = newReviewLater.includes(currentQuestion);

    if (wasMarkedForReview) {
      // Remove from review list (bỏ đánh dấu)
      newReviewLater.splice(newReviewLater.indexOf(currentQuestion), 1);
    } else {
      // Add to review list (đánh dấu)
      newReviewLater.push(currentQuestion);
    }
    setReviewLaterQuestions(newReviewLater);

    // Handle reviewing mode logic
    if (isReviewingMode && wasMarkedForReview) {
      // If we're in reviewing mode and user clicked "Bỏ đánh dấu"
      // Navigate to next purple question (same as when selecting answer)
      setIsAutoAdvancing(true);
      setTimeout(() => {
        if (newReviewLater.length > 0) {
          // Go to next purple question
          const nextReviewQuestion = findNextReviewQuestion(
            currentQuestion,
            newReviewLater
          );
          setCurrentQuestion(nextReviewQuestion);
        } else {
          // No more review questions, go to last question and exit review mode
          setCurrentQuestion(questions.length - 1);
          setIsReviewingMode(false);
        }

        setIsAutoAdvancing(false);
      }, 200);
    } else {
      // Normal mode or marking for review - show notification and stay at current question
      setShowNeedAnswerNotification(true);

      // Exit reviewing mode when user marks question for review
      setIsReviewingMode(false);
    }
  };

  const goToQuestion = (index) => {
    setCurrentQuestion(index);
    setShowNeedAnswerNotification(false); // Hide notification when jumping to specific question
    setIsReviewingMode(false); // Exit reviewing mode when jumping to specific question
  };

  // Helper function to find the next purple question (review later)
  const findNextReviewQuestion = (currentIndex, reviewList) => {
    // Find the next review question after currentIndex
    const nextReviewQuestions = reviewList.filter((q) => q > currentIndex);
    if (nextReviewQuestions.length > 0) {
      return Math.min(...nextReviewQuestions);
    }

    // If no review questions after current, find the first one overall
    const allReviewQuestions = reviewList.filter((q) => q !== currentIndex);
    if (allReviewQuestions.length > 0) {
      return Math.min(...allReviewQuestions);
    }

    // If no more review questions, return last question
    return questions.length - 1;
  };

  const finishTest = () => {
    const answeredCount = answers.filter(
      (a) => a !== undefined && a !== null
    ).length;
    if (answeredCount < questions.length) {
      setShowIncompleteModal(true);
      return;
    }

    // Check if there are questions marked for review
    if (reviewLaterQuestions.length > 0) {
      setShowReviewModal(true);
      return;
    }

    calculateResult();
  };

  const handleForceSubmit = () => {
    setShowReviewModal(false);
    setIsReviewingMode(false); // Exit reviewing mode when force submit
    calculateResult();
  };

  const handleGoToReview = () => {
    setShowReviewModal(false);
    setIsReviewingMode(true); // Enable reviewing mode
    // Go to the first review question (smallest index)
    const firstReviewQuestion = Math.min(...reviewLaterQuestions);
    setCurrentQuestion(firstReviewQuestion);
  };

  const selectAnswer = (answerIndex) => {
    const newAnswers = [...answers];
    newAnswers[currentQuestion] = answerIndex;
    setAnswers(newAnswers);

    // Hide notification if it was showing
    setShowNeedAnswerNotification(false);

    // Handle reviewing mode logic
    if (isReviewingMode) {
      // Remove current question from review list (turn green)
      const newReviewLater = reviewLaterQuestions.filter(
        (q) => q !== currentQuestion
      );
      setReviewLaterQuestions(newReviewLater);

      setIsAutoAdvancing(true);
      setTimeout(() => {
        if (newReviewLater.length > 0) {
          // Go to next purple question
          const nextReviewQuestion = findNextReviewQuestion(
            currentQuestion,
            newReviewLater
          );
          setCurrentQuestion(nextReviewQuestion);
        } else {
          // No more review questions, go to last question and exit review mode
          setCurrentQuestion(questions.length - 1);
          setIsReviewingMode(false);
        }

        setIsAutoAdvancing(false);
      }, 200);
    } else {
      // Normal mode - Auto-advance logic - chỉ đi sequential, không đi đến review questions
      setIsAutoAdvancing(true);
      setTimeout(() => {
        if (currentQuestion < questions.length - 1) {
          // Go to next sequential question
          setCurrentQuestion(currentQuestion + 1);
        } else {
          // Go to last question for submit
          setCurrentQuestion(questions.length - 1);
        }

        setIsAutoAdvancing(false);
      }, 200);
    }
  };

  const calculateResult = () => {
    let correct = 0;
    for (let i = 0; i < questions.length; i++) {
      if (answers[i] === questions[i].correctAnswer) {
        correct++;
      }
    }

    // Calculate IQ score based on percentage
    const percentage = (correct / questions.length) * 100;
    let iq = 70; // Base IQ

    if (percentage >= 90) iq = 140;
    else if (percentage >= 80) iq = 130;
    else if (percentage >= 70) iq = 120;
    else if (percentage >= 60) iq = 110;
    else if (percentage >= 50) iq = 100;
    else if (percentage >= 40) iq = 95;
    else if (percentage >= 30) iq = 90;
    else if (percentage >= 20) iq = 85;
    else if (percentage >= 10) iq = 80;
    else iq = 75;

    const detailsString = `Phân tích chi tiết:
• Số câu đúng: ${correct}/${questions.length} (${percentage.toFixed(1)}%)
• Xếp hạng: ${getIQLevel(iq)}
• Khả năng: ${getIQDescription(iq)}`;

    // Navigate to test result page with data
    navigate("/test-result", {
      state: {
        testType: "iq",
        result: iq,
        details: getIQDescription(iq),
        breakdown: detailsString,
      },
    });
  };

  const getIQDescription = (score) => {
    if (score >= 140) return "Thiên tài - Trí tuệ xuất sắc";
    if (score >= 130) return "Rất thông minh - Có khả năng học tập xuất sắc";
    if (score >= 120) return "Thông minh trên trung bình - Khả năng tư duy tốt";
    if (score >= 110) return "Trung bình khá - Khả năng học tập tốt";
    if (score >= 100) return "Trung bình - Khả năng học tập bình thường";
    if (score >= 90) return "Dưới trung bình - Cần rèn luyện thêm";
    return "Cần cải thiện - Nên tham gia các khóa học bổ trợ";
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (password === correctPassword) {
      setCurrentScreen("result");
      setPasswordError("");

      // Track password verification
      await trackPasswordVerification('iq');
    } else {
      setPasswordError("Mật khẩu không chính xác!");
    }
  };

  const getIQLevel = (score) => {
    if (score >= 140) return "Thiên tài";
    if (score >= 130) return "Rất xuất sắc";
    if (score >= 120) return "Xuất sắc";
    if (score >= 110) return "Khá cao";
    if (score >= 100) return "Trung bình cao";
    if (score >= 90) return "Trung bình";
    if (score >= 80) return "Dưới trung bình";
    return "Thấp";
  };

  const getQuestionStatus = (index) => {
    if (reviewLaterQuestions.includes(index)) {
      return answers[index] !== undefined ? "completed-review" : "review";
    }
    if (answers[index] !== undefined) return "completed";
    return "unanswered";
  };

  const getQuestionStatusColor = (status) => {
    switch (status) {
      case "completed":
        return "bg-green-500 text-white";
      case "review":
        return "bg-purple-500 text-white";
      case "completed-review":
        return "bg-purple-500 text-white";
      default:
        return "bg-white border-2 border-gray-300 text-gray-700";
    }
  };

  const ModernQuestionBoard = () => (
    <div className="bg-white rounded-xl shadow-lg px-5 py-5 sticky top-8">
      <div className="mb-4">
        <h3 className="text-base font-semibold text-gray-800 text-center">
          Bảng theo dõi
        </h3>
      </div>

      <div className="grid grid-cols-7 gap-2 mb-5">
        {questions.map((_, index) => (
          <button
            key={index}
            onClick={() => goToQuestion(index)}
            className={`w-8 h-8 rounded-full text-xs font-semibold transition-all duration-200 hover:scale-105 ${
              index === currentQuestion
                ? "ring-2 ring-blue-400 ring-offset-1 " +
                  getQuestionStatusColor(getQuestionStatus(index))
                : getQuestionStatusColor(getQuestionStatus(index))
            }`}
          >
            {index + 1}
          </button>
        ))}
      </div>

      <button
        onClick={finishTest}
        className="w-full bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white font-bold py-2.5 px-3 rounded-lg transition-all duration-200 transform hover:scale-105 shadow-lg text-sm mb-5"
      >
        NỘP BÀI
      </button>

      <div className="grid grid-cols-3 gap-1 text-xs">
        <div className="flex flex-col items-center">
          <div className="w-2.5 h-2.5 bg-green-500 rounded-full mb-1"></div>
          <span className="text-gray-600">Đã làm</span>
        </div>
        <div className="flex flex-col items-center">
          <div className="w-2.5 h-2.5 bg-gray-300 rounded-full mb-1"></div>
          <span className="text-gray-600">Chưa làm</span>
        </div>
        <div className="flex flex-col items-center">
          <div className="w-2.5 h-2.5 bg-purple-500 rounded-full mb-1"></div>
          <span className="text-gray-600">Cần kiểm tra</span>
        </div>
      </div>
    </div>
  );

  if (currentScreen === "intro") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 py-3">
        {/* Background decorative elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-gradient-to-br from-blue-200 to-purple-200 rounded-full opacity-30 animate-pulse"></div>
          <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-gradient-to-br from-indigo-200 to-blue-200 rounded-full opacity-30 animate-pulse delay-1000"></div>
        </div>

        <div className="relative z-10 container mx-auto px-4 py-12">
          <div className="max-w-4xl mx-auto text-center">
            {/* Header */}
            <div className="mb-8">
              <span className="inline-block px-4 py-2 bg-gradient-to-r from-blue-500 to-indigo-500 text-white text-sm font-medium rounded-full mb-4">
                ✨ Bài test IQ chính thức
              </span>

              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                Test Chỉ Số <span className="text-blue-600">IQ</span>
              </h1>

              <p className="text-lg text-gray-600 mb-12 max-w-2xl mx-auto">
                Đánh giá chỉ số thông minh của bạn với bài test được thiết kế
                theo tiêu chuẩn quốc tế
              </p>
            </div>

            {/* Main card */}
            <div className="bg-white rounded-2xl shadow-xl p-8 mb-8 border border-gray-100">
              <h2 className="text-2xl font-semibold text-gray-800 mb-8">
                Thông tin bài Test IQ chuẩn Quốc Tế
              </h2>

              <div className="grid md:grid-cols-3 gap-6 mb-8">
                <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-xl p-6 text-center">
                  <div className="text-3xl font-bold text-blue-600 mb-2">
                    {questions.length}
                  </div>
                  <div className="text-blue-700 font-medium">Câu hỏi</div>
                </div>

                <div className="bg-gradient-to-r from-purple-50 to-purple-100 rounded-xl p-6 text-center">
                  <div className="text-3xl font-bold text-purple-600 mb-2">
                    20
                  </div>
                  <div className="text-purple-700 font-medium">Phút</div>
                </div>

                <div className="bg-gradient-to-r from-green-50 to-green-100 rounded-xl p-6 text-center">
                  <div className="text-3xl font-bold text-green-600 mb-2">
                    140+
                  </div>
                  <div className="text-green-700 font-medium">IQ tối đa</div>
                </div>
              </div>

              {/* CTA Button */}
              <button
                onClick={startTest}
                className="group relative bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white font-bold text-lg py-4 px-12 rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 mx-auto"
              >
                <span className="flex items-center justify-center">
                  Bắt đầu làm bài ngay
                </span>
              </button>

              <p className="text-sm text-gray-500 mt-4">
                Miễn phí • Không cần đăng ký • Kết quả tức thì
              </p>
            </div>

            {/* Guidelines */}
            <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100 text-left">
              <div className="flex items-center mb-8">
                <div className="w-12 h-12 bg-yellow-500 rounded-xl flex items-center justify-center mr-4">
                  <span className="text-2xl">💡</span>
                </div>
                <h3 className="text-2xl font-semibold text-gray-800">
                  Lưu ý quan trọng
                </h3>
              </div>

              <div className="grid md:grid-cols-3 gap-6">
                <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-xl p-6 text-center">
                  <div className="w-12 h-12 bg-blue-500 rounded-xl flex items-center justify-center mb-4 mx-auto">
                    <span className="text-white font-bold">1</span>
                  </div>
                  <h4 className="font-semibold text-blue-700 mb-3">
                    Tập trung cao độ
                  </h4>
                  <p className="text-gray-600 text-sm">
                    Làm bài trong môi trường yên tĩnh, không bị gián đoạn
                  </p>
                </div>

                <div className="bg-gradient-to-r from-green-50 to-green-100 rounded-xl p-6 text-center">
                  <div className="w-12 h-12 bg-green-500 rounded-xl flex items-center justify-center mb-4 mx-auto">
                    <span className="text-white font-bold">2</span>
                  </div>
                  <h4 className="font-semibold text-green-700 mb-3">
                    Thời gian hợp lý
                  </h4>
                  <p className="text-gray-600 text-sm">
                    Phân bổ thời gian đều cho từng câu hỏi, không nên quá chậm
                  </p>
                </div>

                <div className="bg-gradient-to-r from-purple-50 to-purple-100 rounded-xl p-6 text-center">
                  <div className="w-12 h-12 bg-purple-500 rounded-xl flex items-center justify-center mb-4 mx-auto">
                    <span className="text-white font-bold">3</span>
                  </div>
                  <h4 className="font-semibold text-purple-700 mb-3">
                    Không tra cứu
                  </h4>
                  <p className="text-gray-600 text-sm">
                    Không sử dụng máy tính hoặc tài liệu tham khảo
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (currentScreen === "test") {
    // Don't render if questions not loaded yet
    if (isLoading || questions.length === 0) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center">
          <div className="text-center max-w-md mx-auto p-6">
            {loadError ? (
              <>
                <div className="text-red-500 text-6xl mb-4">⚠️</div>
                <h3 className="text-xl font-bold text-gray-800 mb-2">
                  Lỗi tải dữ liệu
                </h3>
                <p className="text-gray-600 mb-4">
                  Không thể tải câu hỏi từ server: {loadError}
                </p>
                <button
                  onClick={() => window.location.reload()}
                  className="bg-blue-500 hover:bg-blue-600 text-white font-bold py-2 px-4 rounded"
                >
                  Thử lại
                </button>
              </>
            ) : (
              <>
                <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500 mx-auto"></div>
                <p className="mt-4 text-lg text-gray-600">
                  🔄 Đang tải câu hỏi IQ từ database...
                </p>
                <p className="mt-2 text-sm text-gray-500">
                  Vui lòng chờ trong giây lát
                </p>
                <p className="mt-2 text-xs text-gray-400">
                  Đã tải: {questions.length}/25 câu
                </p>
              </>
            )}
          </div>
        </div>
      );
    }

    const answeredCount = answers.filter(
      (a) => a !== undefined && a !== null
    ).length;
    const progress = (answeredCount / questions.length) * 100;

    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 py-3">
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-3 gap-4 md:gap-8">
              {/* Main Content */}
              <div className="col-span-2">
                {/* Header */}
                <div className="bg-white rounded-lg shadow-lg p-4 md:p-5 mb-4">
                  <div className="flex justify-between items-center mb-4">
                    <div className="flex items-center space-x-3">
                      <h2 className="text-xl md:text-2xl font-bold text-gray-800">
                        Test IQ chuẩn Quốc tế
                      </h2>
                      {isReviewingMode && (
                        <div className="bg-purple-100 text-purple-700 px-3 py-1 rounded-full text-sm font-medium animate-pulse">
                          🔍 Đang kiểm tra lại
                        </div>
                      )}
                    </div>
                    <div className="bg-red-100 text-red-600 px-4 py-2 rounded-full text-base font-bold">
                      ⏱️ {formatTime(timeLeft)}
                    </div>
                  </div>

                  {/* Progress Bar */}
                  <div className="w-full bg-gray-200 rounded-full h-3 mb-4">
                    <div
                      className="bg-gradient-to-r from-blue-500 to-indigo-600 h-3 rounded-full transition-all duration-300"
                      style={{ width: `${progress}%` }}
                    ></div>
                  </div>

                  <div className="flex justify-between text-sm text-gray-600">
                    <span>
                      Tiến độ: {answeredCount} / {questions.length}
                    </span>
                    <span>Câu hiện tại: {currentQuestion + 1}</span>
                  </div>
                </div>



                {/* Question */}
                <div className="bg-white rounded-lg shadow-lg p-5 mb-4">
                  <div className="flex items-center mb-4">
                    <span className="bg-blue-100 text-blue-600 px-3 py-1 rounded-full text-sm font-semibold mr-4">
                      {currentQuestion + 1}
                    </span>
                    <h3 className="text-xl font-semibold text-gray-800">
                      {questions[currentQuestion]?.question ||
                        "Đang tải câu hỏi..."}
                    </h3>
                  </div>

                  <div className="space-y-4">
                    {questions[currentQuestion]?.options?.map(
                      (option, index) => (
                        <button
                          key={`${currentQuestion}-${index}-${
                            option.text || index
                          }`}
                          onClick={() => {
                            selectAnswer(index);
                            handleAnswer(index);
                          }}
                          className={`w-full text-left p-4 rounded-lg border-2 transition-all duration-200 ${
                            answers[currentQuestion] === index
                              ? "border-blue-500 bg-blue-50 text-blue-800 shadow-sm"
                              : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                          }`}
                        >
                          <div className="flex items-center">
                            <div
                              className={`w-8 h-8 rounded-full border-2 mr-4 flex items-center justify-center ${
                                answers[currentQuestion] === index
                                  ? "border-blue-500 bg-blue-500"
                                  : "border-gray-300"
                              }`}
                            >
                              <span
                                className={`text-base font-bold ${
                                  answers[currentQuestion] === index
                                    ? "text-white"
                                    : "text-gray-600"
                                }`}
                              >
                                {String.fromCharCode(65 + index)}
                              </span>
                            </div>
                            <span className="text-gray-800 text-lg">
                              {option.text || "Đang tải..."}
                            </span>
                          </div>
                        </button>
                      )
                    ) || (
                      <div className="text-center text-gray-500 py-8">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-4"></div>
                        Đang tải options...
                      </div>
                    )}
                  </div>

                  {/* Review Later Button Area - Mobile Responsive */}
                  <div className="mt-8 space-y-4">
                    {/* Review Later Button */}
                    <div className="flex justify-center">
                      <button
                        onClick={toggleReviewLater}
                        className={`px-3 py-2 sm:px-4 sm:py-2 rounded-lg font-medium transition-all duration-200 text-xs sm:text-sm ${
                          reviewLaterQuestions.includes(currentQuestion)
                            ? "bg-purple-500 text-white hover:bg-purple-600"
                            : "bg-purple-100 text-purple-700 hover:bg-purple-200"
                        }`}
                      >
                        {reviewLaterQuestions.includes(currentQuestion)
                          ? "Bỏ đánh dấu"
                          : "Lát kiểm tra lại"}
                      </button>
                    </div>

                    {/* Answer Required Notification - Mobile Responsive */}
                    {showNeedAnswerNotification && (
                      <div className="flex justify-center px-4">
                        <div className="flex items-center space-x-2 bg-blue-600 text-white px-3 py-2 sm:px-4 sm:py-2 rounded-lg shadow-md border border-blue-700 text-center max-w-full">
                          <svg
                            className="w-4 h-4 flex-shrink-0"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                          </svg>
                          <span className="text-xs sm:text-sm font-medium break-words">
                            Vui lòng chọn trước một đáp án để tiếp tục!
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Navigation */}
                <div className="bg-white rounded-lg shadow-lg p-4 sticky bottom-4 z-10">
                  <div className="flex justify-between items-center">
                    <button
                      onClick={prevQuestion}
                      disabled={currentQuestion === 0}
                      className="flex items-center px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 text-sm"
                    >
                      ← Câu trước
                    </button>

                    <div className="flex gap-3">
                      {isAutoAdvancing ? (
                        <div className="px-4 py-2 bg-blue-100 text-blue-600 rounded-lg text-sm font-medium flex items-center">
                          <svg
                            className="animate-spin -ml-1 mr-2 h-4 w-4 text-blue-600"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                          Đang chuyển...
                        </div>
                      ) : currentQuestion === questions.length - 1 ? (
                        <button
                          onClick={finishTest}
                          className="px-4 py-2 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg hover:from-green-600 hover:to-green-700 font-medium transition-all duration-200 text-sm"
                        >
                          Hoàn thành
                        </button>
                      ) : (
                        <button
                          onClick={nextQuestion}
                          className="px-4 py-2 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-lg hover:from-blue-600 hover:to-indigo-700 font-medium transition-all duration-200 text-sm"
                        >
                          Câu tiếp →
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Modern Question Board - Always visible sidebar */}
              <div>
                <ModernQuestionBoard />
              </div>
            </div>
          </div>
        </div>

        {/* Incomplete Modal */}
        {showIncompleteModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl p-6 mx-4 max-w-md w-full">
              <div className="text-center">
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-orange-100 mb-4">
                  <svg
                    className="h-6 w-6 text-orange-600"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Chưa hoàn thành đủ câu hỏi
                </h3>
                <p className="text-sm text-gray-500 mb-4">
                  Bạn cần hoàn thành tất cả {questions.length} câu hỏi trước khi
                  nộp bài! Hiện tại bạn đã làm{" "}
                  {answers.filter((a) => a !== undefined && a !== null).length}/
                  {questions.length} câu.
                </p>
                <button
                  type="button"
                  className="w-full bg-blue-600 text-white px-4 py-2 rounded-md font-medium hover:bg-blue-700 transition-colors"
                  onClick={() => setShowIncompleteModal(false)}
                >
                  Tiếp tục làm bài
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Review Later Modal */}
        {showReviewModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl p-6 mx-4 max-w-md w-full">
              <div className="text-center">
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-purple-100 mb-4">
                  <svg
                    className="h-6 w-6 text-purple-600"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Còn câu hỏi cần kiểm tra lại
                </h3>
                <p className="text-sm text-gray-500 mb-4">
                  Bạn có {reviewLaterQuestions.length} câu hỏi được đánh dấu cần
                  kiểm tra lại. Bạn có muốn xem lại những câu này trước khi nộp
                  bài không?
                </p>
                <div className="space-y-3">
                  <button
                    type="button"
                    className="w-full bg-purple-600 text-white px-4 py-2 rounded-md font-medium hover:bg-purple-700 transition-colors"
                    onClick={handleGoToReview}
                  >
                    Kiểm tra lại
                  </button>
                  <button
                    type="button"
                    className="w-full bg-gray-200 text-gray-800 px-4 py-2 rounded-md font-medium hover:bg-gray-300 transition-colors"
                    onClick={handleForceSubmit}
                  >
                    Chấp nhận và nộp bài
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return null;
};

export default TestIQ;
